import React from 'react';
import { Box, Typography } from '@mui/material';
import { toast, type Id } from 'react-toastify';
import DosenConfirmModal from '../../../components/DosenConfirmModal';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';

/** What an editor is asking before an edit that costs a capability (docs/PRD-capability-lesson-roleplays.md R5, R9). */
export interface CapabilityConfirmRequest {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  /** A second way out, beside the main one (e.g. Remove Roleplay beside Regenerate). */
  alt?: { label: string; onClick: () => void };
  /** One of the actions must be taken: no Cancel and no close (R9: an old Roleplay can't stay). */
  required?: boolean;
}

/**
 * The confirm an editor shows before a delete, retag or Roleplay change that
 * costs a capability its last lesson or its last Scored Roleplay. Cancel
 * leaves everything as it was.
 */
const CapabilityConfirm: React.FC<{
  request: CapabilityConfirmRequest | null;
  onClose: () => void;
}> = ({ request, onClose }) => {
  const act = (action?: () => void) => {
    onClose();
    action?.();
  };
  return (
    <DosenConfirmModal
      open={Boolean(request)}
      onClose={request?.required ? () => undefined : onClose}
      hideClose={request?.required}
      title={request?.title ?? ''}
    >
      <Typography
        sx={{
          color: 'var(--Text-text-secondary, #B6B6B6)',
          fontSize: 14,
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          lineHeight: '20px'
        }}
      >
        {request?.body}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {!request?.required && (
          <DosenButton tone="basic" sizeVariant="sm" onClick={onClose}>
            Cancel
          </DosenButton>
        )}
        {request?.alt && (
          <DosenButton tone="basic" sizeVariant="sm" onClick={() => act(request.alt?.onClick)}>
            {request.alt.label}
          </DosenButton>
        )}
        <DosenButton sizeVariant="sm" onClick={() => act(request?.onConfirm)}>
          {request?.confirmLabel}
        </DosenButton>
      </Box>
    </DosenConfirmModal>
  );
};

export default CapabilityConfirm;

/** A toast with an Undo button, for a delete or a Roleplay switched off (R5, R9). Returns its id, to dismiss it. */
export const toastWithUndo = (message: string, onUndo: () => void): Id =>
  toast.info(
    ({ closeToast }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontFamily: 'Inter', flex: 1 }}>{message}</Typography>
        <Box
          component="button"
          type="button"
          onClick={() => {
            onUndo();
            closeToast?.();
          }}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'Inter',
            color: 'var(--Stroke-highlight, #F79420)',
            p: 0
          }}
        >
          Undo
        </Box>
      </Box>
    ),
    { autoClose: 10000, closeOnClick: false }
  );
